/**
 * The deterministic fixed-step simulator — the heart of SKYPULSE.
 *
 * R2 time model (brief §1.2, amending D-001):
 *  - Sim ticks advance in REAL time (120 Hz wall-clock) — steering and
 *    gravity never dilate with throttle.
 *  - songTime = ∫ effRate · d(realTime), integrated here as an exact per-tick
 *    running sum (effRate is constant within a tick). Forward progress is
 *    z = baseSpeed × songTime — still derived, never integrated in z-space.
 *  - effRate = clamp(throttle × padFactor, EFFRATE_MIN..MAX). Pads are
 *    multiplicative rate modifiers with songTime-based windows (supersedes
 *    D-019).
 *
 * Determinism rules unchanged: no wall clock, no Math.random, no render
 * state; only +,-,×,÷ and comparisons; same inputs ⇒ bit-identical state.
 */
import {
  SIM_DT,
  ROW_DEPTH,
  LANES,
  CELL_WIDTH,
  laneCenterX,
  STEER_MAX_SPEED,
  STEER_RAMP_MS,
  X_LIMIT,
  type JumpParams,
  COYOTE_TICKS,
  JUMP_BUFFER_TICKS,
  SUPPORT_HALF_WIDTH,
  HIT_HALF_WIDTH,
  NOSE_LENGTH,
  STEP_TOLERANCE,
  STEP_DOWN_TOLERANCE,
  FALL_DEATH_Y,
  HAZARD_KILL_HEIGHT,
  TUNNEL_ROOF_HEIGHT,
  SHIP_TOP_CLEARANCE,
  ICE_LAT_ACCEL,
  ICE_LAT_DAMP,
  BIT_COLLECT_HEIGHT,
  BLOCK_UNIT_HEIGHT,
  THROTTLE_MIN,
  THROTTLE_MAX,
  THROTTLE_RAMP_PER_S,
  THROTTLE_DETENT_BAND,
  EFFRATE_MIN,
  EFFRATE_MAX,
  PAD_BOOST_FACTOR,
  PAD_STICKY_FACTOR,
  PAD_DURATION_BEATS,
  ON_BEAT_WINDOW_MS,
} from "../config/tuning";
import type { Cell, ParsedLevel } from "../track/types";
import type { StateHasher } from "../core/hash";

/** Max the ship may sink below a surface in one tick and still land on it. */
const MAX_LAND_PENETRATION = 0.3;

export interface InputFrame {
  /** -1 (left) .. 1 (right). Keyboard produces -1|0|1. */
  steer: number;
  jumpHeld: boolean;
  /** Throttle controls (R2): digital hold — the log stays boolean. */
  accel: boolean;
  brake: boolean;
}

export const NEUTRAL_FRAME: InputFrame = { steer: 0, jumpHeld: false, accel: false, brake: false };

export type SimStatus = "running" | "dead" | "finished";
export type DeathCause = "fall" | "frontal" | "hazard" | "tunnel";

export interface SimEvents {
  bitCollected: number; // bit index
  died: DeathCause;
  finished: number; // finish tick
  checkpoint: number; // checkpoint row crossed
  boost: number; // +1 boost, -1 sticky (for FX)
  jumped: boolean; // payload: was it on the beat? (spec 5.3)
  landed: number; // tick
  headBump: number; // tick — roof contact (R2 §2.1, never fatal)
}

/** Pad rate window (songTime-based — the chevrons pulse in beats). */
export interface PadEvent {
  startSongTime: number;
  endSongTime: number;
  factor: number; // PAD_BOOST_FACTOR or PAD_STICKY_FACTOR
}

export interface SimOptions {
  /** Base forward speed at effRate 1 (rowsPerBeat × ROW_DEPTH × bpm/60). */
  speed: number;
  jump: JumpParams;
  /** Seconds per beat of the song. */
  secondsPerBeat: number;
  /** First controlled tick — 0 for full runs, checkpoint tick for respawn/practice. */
  startTick?: number;
}

export class Sim {
  // --- state (everything hashed) ---
  tick: number;
  x = 0;
  vx = 0;
  y = 0;
  vy = 0;
  grounded = true;
  coyoteTicks = 0;
  bufferTicks = 0;
  prevJumpHeld = false;
  status: SimStatus = "running";
  deathCause: DeathCause | null = null;
  deathTick = -1;
  finishTick = -1;
  bitsMask = 0;
  lastCheckpointRow = -1;
  /** The throttle (R2): [0.6, 1.4], inertial ramp, detent at 1.0. */
  throttle = 1;
  /** ∫ effRate · d(realTime) — exact per-tick running sum. */
  songTime: number;
  /** effRate used for the CURRENT tick (recomputed each step). */
  effRate = 1;
  padEvents: PadEvent[] = [];
  /** Jumps executed within ±ON_BEAT_WINDOW_MS of a beat (style bonus, 5.3). */
  onBeatJumps = 0;
  /** Grounded on ice this tick (drift physics + slide SFX loop). */
  onIce = false;
  private bumpedThisAir = false;
  private triggeredPads = new Set<number>();

  /** Previous-tick pose, for render interpolation only (not part of sim logic). */
  prevX = 0;
  prevY = 0;

  readonly speed: number;
  readonly startTick: number;
  private readonly jump: JumpParams;
  private readonly secondsPerBeat: number;
  private readonly level: ParsedLevel;
  private listeners: Partial<{ [K in keyof SimEvents]: (payload: SimEvents[K]) => void }> = {};

  constructor(level: ParsedLevel, opts: SimOptions) {
    this.level = level;
    this.speed = opts.speed;
    this.jump = opts.jump;
    this.secondsPerBeat = opts.secondsPerBeat;
    this.startTick = opts.startTick ?? 0;
    this.tick = this.startTick;
    // rate-1 history before the start tick (respawn/practice contract)
    this.songTime = this.startTick * SIM_DT;
    if (this.startTick > 0) {
      this.lastCheckpointRow = Math.floor(this.z / ROW_DEPTH);
    }
  }

  /** First sim tick at or inside the given row, assuming rate-1 history. */
  static tickForRow(row: number, speed: number): number {
    return Math.ceil((row * ROW_DEPTH) / (speed * SIM_DT));
  }

  /** Outbound-only notifications (UI/render). Sim behaviour never depends on them. */
  on<K extends keyof SimEvents>(event: K, cb: (payload: SimEvents[K]) => void): void {
    (this.listeners as Record<K, (payload: SimEvents[K]) => void>)[event] = cb;
  }

  get z(): number {
    return this.speed * this.songTime;
  }

  /** Continuous songTime at a realTime near the current tick (render only). */
  songTimeAt(realT: number): number {
    const extrap = Math.max(-SIM_DT, Math.min(SIM_DT, realT - this.tick * SIM_DT));
    return this.songTime + this.effRate * extrap;
  }

  get beat(): number {
    return this.songTime / this.secondsPerBeat;
  }

  get progressPct(): number {
    const row = Math.floor(this.z / ROW_DEPTH);
    return Math.min(100, Math.max(0, (row / this.level.finishRow) * 100));
  }

  get bitsCollected(): number {
    let n = 0;
    for (let i = 0; i < this.level.bits.length; i++) if (this.bitsMask & (1 << i)) n++;
    return n;
  }

  /** Product of pad factors active at a given songTime. */
  padFactorAt(songTime: number): number {
    let f = 1;
    for (const e of this.padEvents) {
      if (songTime >= e.startSongTime && songTime < e.endSongTime) f *= e.factor;
    }
    return f;
  }

  step(input: InputFrame): void {
    if (this.status !== "running") return;

    this.prevX = this.x;
    this.prevY = this.y;

    const dt = SIM_DT;
    const t = this.tick + 1;

    // 0 — throttle: inertial ramp while held; detent snap on release near 1.0
    const dir = (input.accel ? 1 : 0) - (input.brake ? 1 : 0);
    if (dir !== 0) {
      this.throttle += dir * THROTTLE_RAMP_PER_S * dt;
      if (this.throttle < THROTTLE_MIN) this.throttle = THROTTLE_MIN;
      else if (this.throttle > THROTTLE_MAX) this.throttle = THROTTLE_MAX;
    } else if (
      this.throttle !== 1 &&
      this.throttle >= 1 - THROTTLE_DETENT_BAND &&
      this.throttle <= 1 + THROTTLE_DETENT_BAND
    ) {
      this.throttle = 1; // the musical home speed captures
    }

    // 1 — effRate for this tick, then integrate songTime (exact running sum)
    let rate = this.throttle * this.padFactorAt(this.songTime);
    if (rate < EFFRATE_MIN) rate = EFFRATE_MIN;
    else if (rate > EFFRATE_MAX) rate = EFFRATE_MAX;
    this.effRate = rate;
    this.songTime += rate * dt;
    const z = this.speed * this.songTime;

    // 2 — steering: accel-limited approach to target velocity (REAL time —
    // responsiveness never dilates with throttle). On ice the ship drifts:
    // weaker steering authority, far weaker self-damping (R2 Feature C).
    const centerRowPre = Math.floor(z / ROW_DEPTH);
    this.onIce = this.grounded && this.touchesKind(centerRowPre, "ice", SUPPORT_HALF_WIDTH);
    const steer = input.steer < -1 ? -1 : input.steer > 1 ? 1 : input.steer;
    const targetVx = steer * STEER_MAX_SPEED;
    let maxDv = (STEER_MAX_SPEED / (STEER_RAMP_MS / 1000)) * dt;
    if (this.onIce) maxDv *= steer !== 0 ? ICE_LAT_ACCEL : ICE_LAT_DAMP;
    const dv = targetVx - this.vx;
    this.vx += dv < -maxDv ? -maxDv : dv > maxDv ? maxDv : dv;
    this.x += this.vx * dt;
    if (this.x < -X_LIMIT) {
      this.x = -X_LIMIT;
      this.vx = 0;
    } else if (this.x > X_LIMIT) {
      this.x = X_LIMIT;
      this.vx = 0;
    }

    // 3 — jump buffering (edge-triggered)
    const jumpPressed = input.jumpHeld && !this.prevJumpHeld;
    this.bufferTicks = jumpPressed ? JUMP_BUFFER_TICKS : Math.max(0, this.bufferTicks - 1);

    const centerRow = Math.floor(z / ROW_DEPTH);
    const noseRow = Math.floor((z + NOSE_LENGTH) / ROW_DEPTH);

    // 4 — deadly block contact (tight hitbox). On a ramp the nose rides the
    // slope: project its height, or climbing into a matched block top would
    // read as a wall (R2 Feature C).
    const noseY = this.y + this.rampSlopeAt(centerRow) * NOSE_LENGTH;
    if (this.blockedAt(noseRow, noseY) || this.blockedAt(centerRow, this.y)) {
      this.die("frontal", t);
      return;
    }

    // 5 — support under ship center (forgiving hitbox; ramps interpolate)
    const rowFrac = z / ROW_DEPTH - centerRow;
    const support = this.supportAt(centerRow, this.y, rowFrac);

    // 6 — grounded transitions: snap up small steps AND down ramp slopes
    // (STEP_DOWN_TOLERANCE — a real ledge still drops the ship airborne)
    if (this.grounded) {
      if (
        support !== null &&
        support >= this.y - STEP_DOWN_TOLERANCE &&
        support <= this.y + STEP_TOLERANCE
      ) {
        this.y = support;
        this.vy = 0;
        this.coyoteTicks = COYOTE_TICKS;
      } else {
        this.grounded = false;
        this.vy = 0;
      }
    }

    // 7 — jump execution (same tick as leaving a ledge still counts)
    if (this.bufferTicks > 0 && (this.grounded || this.coyoteTicks > 0)) {
      this.vy = this.jump.jumpV0;
      this.grounded = false;
      this.bufferTicks = 0;
      this.coyoteTicks = 0;
      // on-beat bonus (5.3): distance from the nearest beat in songTime
      const beatF = this.songTime / this.secondsPerBeat;
      const distS = Math.abs(beatF - Math.round(beatF)) * this.secondsPerBeat;
      const onBeat = distS * 1000 <= ON_BEAT_WINDOW_MS;
      if (onBeat) this.onBeatJumps++;
      this.listeners.jumped?.(onBeat);
    }

    // 8 — airborne integration + landing (REAL-time gravity). The landing
    // window scales with fall speed so a single discrete tick can never
    // tunnel through it (fast heavy-gravity descents fall > 0.3 u/tick).
    if (!this.grounded) {
      this.vy -= this.jump.gravity * dt;
      this.y += this.vy * dt;
      this.coyoteTicks = Math.max(0, this.coyoteTicks - 1);
      // worst case: the ship dips just below the plane on the gap's final
      // sliver, then falls one more tick before support exists — 2.2 ticks
      // of fall must fit inside the window
      const landWindow = Math.max(MAX_LAND_PENETRATION, -this.vy * dt * 2.2);
      if (
        this.vy <= 0 &&
        support !== null &&
        this.y <= support &&
        this.y >= support - landWindow
      ) {
        this.y = support;
        this.vy = 0;
        this.grounded = true;
        this.bumpedThisAir = false;
        this.listeners.landed?.(t);
      }
    } else {
      this.bumpedThisAir = false;
    }

    // 8b — roofs (R2 §2.1): entrance rims kill; under a roof, the ship's top
    // head-bumps (vy → 0, fall begins) — never fatal by itself
    const ceilingHere = this.roofedAt(centerRow) ? TUNNEL_ROOF_HEIGHT : Infinity;
    if (
      ceilingHere === Infinity &&
      this.roofedAt(noseRow) &&
      this.y + SHIP_TOP_CLEARANCE > TUNNEL_ROOF_HEIGHT
    ) {
      this.die("frontal", t); // too high at the entrance — the rim face
      return;
    }
    if (ceilingHere !== Infinity && this.y + SHIP_TOP_CLEARANCE > ceilingHere) {
      this.y = ceilingHere - SHIP_TOP_CLEARANCE;
      if (this.vy > 0) this.vy = 0;
      if (!this.bumpedThisAir) {
        this.bumpedThisAir = true;
        this.listeners.headBump?.(t);
      }
    }

    // 9 — pads: grounded contact opens a rate window, once per cell
    if (this.grounded) {
      this.triggerPads(centerRow);
    }

    // 10 — deadly contacts (tunnel-jump death is REPEALED — R2 §2.1)
    if (this.touchesKind(centerRow, "hazard", HIT_HALF_WIDTH) && this.y < HAZARD_KILL_HEIGHT) {
      this.die("hazard", t);
      return;
    }
    if (this.y < FALL_DEATH_Y) {
      this.die("fall", t);
      return;
    }

    // 11 — bits
    if (this.y < BIT_COLLECT_HEIGHT) {
      this.collectBits(centerRow);
    }

    // 12 — checkpoints
    if (
      centerRow > this.lastCheckpointRow &&
      centerRow < this.level.numRows &&
      this.level.cells[centerRow]?.[0]?.kind === "checkpoint"
    ) {
      this.lastCheckpointRow = centerRow;
      this.listeners.checkpoint?.(centerRow);
    }

    // 13 — finish
    if (centerRow >= this.level.finishRow) {
      this.status = "finished";
      this.finishTick = t;
      this.tick = t;
      this.prevJumpHeld = input.jumpHeld;
      this.listeners.finished?.(t);
      return;
    }

    this.prevJumpHeld = input.jumpHeld;
    this.tick = t;
  }

  // -------------------------------------------------------------------------

  private die(cause: DeathCause, tick: number): void {
    this.status = "dead";
    this.deathCause = cause;
    this.deathTick = tick;
    this.tick = tick;
    this.listeners.died?.(cause);
  }

  private cellAt(row: number, lane: number): Cell | null {
    if (row < 0 || row >= this.level.numRows) return null;
    return this.level.cells[row]?.[lane] ?? null;
  }

  /** Walkable top height of a cell, or null if it offers no support.
   *  Ramps interpolate across the row (R2 Feature C, redeeming D-022). */
  private walkableTop(cell: Cell, rowFrac: number): number | null {
    switch (cell.kind) {
      case "empty":
      case "hazard":
      case "tunnelHole": // U: roof present, floor absent (R2 §2.2)
        return null;
      case "block":
        return cell.height * BLOCK_UNIT_HEIGHT;
      case "rampUp":
      case "rampDown":
        return (cell.rampBase ?? 0) + (cell.rampDir ?? 1) * rowFrac;
      default:
        return 0;
    }
  }

  /** Height change per z-unit of the ramp under the ship (0 off-ramp). */
  private rampSlopeAt(row: number): number {
    for (let lane = 0; lane < LANES; lane++) {
      if (!this.overlapsLane(lane, HIT_HALF_WIDTH)) continue;
      const cell = this.cellAt(row, lane);
      if (cell?.rampDir !== undefined) return cell.rampDir / ROW_DEPTH;
    }
    return 0;
  }

  /** Does any tight-overlapped cell in this row carry a roof? */
  private roofedAt(row: number): boolean {
    for (let lane = 0; lane < LANES; lane++) {
      if (!this.overlapsLane(lane, HIT_HALF_WIDTH)) continue;
      const kind = this.cellAt(row, lane)?.kind;
      if (kind === "tunnel" || kind === "tunnelHole") return true;
    }
    return false;
  }

  private overlapsLane(lane: number, halfW: number): boolean {
    const c = laneCenterX(lane);
    return this.x + halfW > c - CELL_WIDTH / 2 && this.x - halfW < c + CELL_WIDTH / 2;
  }

  private supportAt(row: number, y: number, rowFrac: number): number | null {
    let best: number | null = null;
    for (let lane = 0; lane < LANES; lane++) {
      if (!this.overlapsLane(lane, SUPPORT_HALF_WIDTH)) continue;
      const cell = this.cellAt(row, lane);
      if (!cell) continue;
      const top = this.walkableTop(cell, rowFrac);
      if (top === null || top > y + STEP_TOLERANCE) continue;
      if (best === null || top > best) best = top;
    }
    return best;
  }

  private blockedAt(row: number, y: number): boolean {
    for (let lane = 0; lane < LANES; lane++) {
      if (!this.overlapsLane(lane, HIT_HALF_WIDTH)) continue;
      const cell = this.cellAt(row, lane);
      if (!cell || cell.kind !== "block") continue;
      if (cell.height * BLOCK_UNIT_HEIGHT - y > STEP_TOLERANCE) return true;
    }
    return false;
  }

  private touchesKind(row: number, kind: Cell["kind"], halfW: number): boolean {
    for (let lane = 0; lane < LANES; lane++) {
      if (!this.overlapsLane(lane, halfW)) continue;
      if (this.cellAt(row, lane)?.kind === kind) return true;
    }
    return false;
  }

  private triggerPads(row: number): void {
    for (let lane = 0; lane < LANES; lane++) {
      if (!this.overlapsLane(lane, SUPPORT_HALF_WIDTH)) continue;
      const cell = this.cellAt(row, lane);
      if (!cell || (cell.kind !== "boost" && cell.kind !== "sticky")) continue;
      const key = row * LANES + lane;
      if (this.triggeredPads.has(key)) continue;
      this.triggeredPads.add(key);
      const factor = cell.kind === "boost" ? PAD_BOOST_FACTOR : PAD_STICKY_FACTOR;
      this.padEvents.push({
        startSongTime: this.songTime,
        endSongTime: this.songTime + PAD_DURATION_BEATS * this.secondsPerBeat,
        factor,
      });
      this.listeners.boost?.(factor > 1 ? 1 : -1);
    }
  }

  private collectBits(row: number): void {
    for (let lane = 0; lane < LANES; lane++) {
      if (!this.overlapsLane(lane, SUPPORT_HALF_WIDTH)) continue;
      const cell = this.cellAt(row, lane);
      if (!cell || cell.kind !== "bit" || cell.bitIndex < 0) continue;
      const bit = 1 << cell.bitIndex;
      if (!(this.bitsMask & bit)) {
        this.bitsMask |= bit;
        this.listeners.bitCollected?.(cell.bitIndex);
      }
    }
  }

  /** Feed the full sim state into a hasher (determinism tests, replays). */
  hashInto(h: StateHasher): void {
    h.addInt(this.tick)
      .addInt(this.startTick)
      .addFloat(this.x)
      .addFloat(this.vx)
      .addFloat(this.y)
      .addFloat(this.vy)
      .addFloat(this.throttle)
      .addFloat(this.songTime)
      .addFloat(this.effRate)
      .addBool(this.grounded)
      .addInt(this.coyoteTicks)
      .addInt(this.bufferTicks)
      .addBool(this.prevJumpHeld)
      .addInt(this.bitsMask)
      .addInt(this.status === "running" ? 0 : this.status === "dead" ? 1 : 2)
      .addInt(this.deathTick)
      .addInt(this.finishTick)
      .addInt(this.lastCheckpointRow)
      .addInt(this.onBeatJumps)
      .addInt(this.padEvents.length);
    for (const e of this.padEvents) {
      h.addFloat(e.startSongTime).addFloat(e.endSongTime).addFloat(e.factor);
    }
  }
}
